import type { Surcharge } from '@api/useFreight/types'
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import { getDateFilterParams } from './dateUtils'
import {
  ActiveRenderer,
  RegionRenderer,
  ProductGroupRenderer,
  TypeRenderer,
  SurchargeRateRenderer,
  UpdatedTagRenderer,
  DateRenderer,
  EffectiveToRenderer,
  NextRateRenderer,
  ActionButtons,
} from '../components'
import React from 'react'

/**
 * Creates column definitions for the surcharge grid
 * @returns Array of column definitions
 */
export const createSurchargeColumnDefs = (): ColDef<Surcharge>[] => [
  {
    headerName: 'Active',
    field: 'disabled',
    maxWidth: 100,
    minWidth: 100,
    filter: 'agSetColumnFilter',
    filterValueGetter: (params: ValueGetterParams<Surcharge>) => (!params.data?.disabled ? 'active' : 'inactive'),
    filterParams: {
      values: ['active', 'inactive'],
      valueFormatter: (params: { value: string }) => (params.value === 'active' ? 'Active' : 'Inactive'),
    },
    cellRenderer: ActiveRenderer,
  },
  {
    headerName: 'Region',
    field: 'freight_region_name',
    cellRenderer: RegionRenderer,
  },
  {
    headerName: 'Product Group',
    field: 'product_group',
    cellRenderer: ProductGroupRenderer,
  },
  {
    headerName: 'Type',
    field: 'type',
    cellRenderer: TypeRenderer,
  },
  {
    headerName: 'Surcharge Rate',
    field: 'rates',
    valueGetter: ({ data: rowData }: ValueGetterParams<Surcharge>) => {
      const value = rowData?.current_surcharge
      if (value === null || value === undefined) return null
      const type = rowData?.type || 'Percentage'
      return type === 'Percentage' ? parseFloat((value * 100).toFixed(3)) : value
    },
    type: 'numericColumn',
    filter: 'agNumberColumnFilter',
    filterParams: {
      defaultOption: 'equals',
      allowedCharPattern: '\\d\\-\\.',
      numberParser: (text: string) => {
        if (!text) return null
        const cleanText = text.replace(/[^0-9.-]/g, '')
        if (!cleanText) return null
        return Number(cleanText)
      },
    },
    cellRenderer: SurchargeRateRenderer,
  },
  {
    headerName: 'Updated',
    field: 'updated_on',
    filter: 'agDateColumnFilter',
    sortable: true,
    minWidth: 150,
    valueGetter: ({ data: rowData }: ValueGetterParams<Surcharge>) => {
      return rowData?.updated_on ? new Date(rowData.updated_on) : null
    },
    filterParams: getDateFilterParams(),
    cellRenderer: UpdatedTagRenderer,
  },
  {
    headerName: 'Effective From',
    field: 'rates',
    filter: 'agDateColumnFilter',
    sortable: true,
    valueGetter: ({ data: rowData }: ValueGetterParams<Surcharge>) =>
      rowData?.rates?.[0]?.effective_from ? new Date(rowData.rates[0].effective_from) : null,
    filterParams: getDateFilterParams(),
    cellRenderer: DateRenderer,
  },
  {
    headerName: 'Effective To',
    field: 'rates',
    filter: 'agDateColumnFilter',
    sortable: true,
    valueGetter: ({ data: rowData }: ValueGetterParams<Surcharge>) =>
      rowData?.rates?.[0]?.effective_to ? new Date(rowData.rates[0].effective_to) : null,
    filterParams: getDateFilterParams(),
    cellRenderer: EffectiveToRenderer,
  },
  {
    headerName: 'Next Rate',
    field: 'next_scheduled_rate',
    filter: 'agDateColumnFilter',
    sortable: true,
    valueGetter: ({ data: rowData }: ValueGetterParams<Surcharge>) =>
      rowData?.next_scheduled_rate?.effective_from ? new Date(rowData.next_scheduled_rate.effective_from) : null,
    filterParams: getDateFilterParams(),
    cellRenderer: NextRateRenderer,
  },
  {
    headerName: 'Actions',
    maxWidth: 100,
    minWidth: 100,
    filter: false,
    cellRenderer: (params: ICellRendererParams<Surcharge>) => (
      <ActionButtons
        params={params}
        onEdit={params.context?.onEdit}
        onViewRateHistory={params.context?.onViewRateHistory}
      />
    ),
  },
]
